import { DbetSingleton, saveState } from 'utils';
import {
  SYNC,
  SEND_TO,
  DEPOSIT,
  WITHDRAW,
  CANCEL_WITHDRAW,
  PREPARE_WITHDRAW,
  CREATE_CHANNEL,
} from 'constants/dbetSDK';
import { DBET_USER_DATA } from 'constants/auth';
import { LOADED, FAILED } from 'constants/data';
import {
  SET_TRANSACTION,
  SET_DBET_DATA,
  SET_PROVIDER_DATA,
  IS_DBET_LOADED,
  CLEAR_NOTIFICATION,
  WAIT_FOR_TRANSACTION,
  SET_FINAL_TRANSACTION,
  SET_CONTRACT_TRANSACTION,
} from './actionTypes';
import { setUserSetting } from './auth';
import { setError } from './error';

const dbet = DbetSingleton.getInstance();

const setTransaction = payload => ({
  type: SET_TRANSACTION,
  payload,
});

export const clearNotification = payload => ({
  type: CLEAR_NOTIFICATION,
  payload,
});

export const setFinalTransaction = payload => ({
  type: SET_FINAL_TRANSACTION,
  payload,
});

export const dbetIsLoaded = () => ({
  type: IS_DBET_LOADED,
});

export const setDbetData = payload => ({
  type: SET_DBET_DATA,
  payload,
});

export const setProviderData = payload => ({
  type: SET_PROVIDER_DATA,
  payload,
});

export const setContractTransaction = payload => ({
  type: SET_CONTRACT_TRANSACTION,
  payload,
});

export const setWaitForTransaction = payload => ({
  type: WAIT_FOR_TRANSACTION,
  payload,
});

export const setTransactionNotification =
  (payload, timeOut = 5000) =>
  dispatch => {
    dispatch(setTransaction(payload));

    setTimeout(() => dispatch(clearNotification(payload.type)), timeOut);
  };

const setTransactionByType = payload => async dispatch => {
  const { type, transaction } = payload;
  let hasChannel = await dbet.hasChannel();

  if (transaction.result) {
    const { hasError, result } = await dbet.waitForTransaction(transaction.result);

    if (!hasChannel) {
      const { result } = await dbet.finishLogin();

      saveState(DBET_USER_DATA, result, 'sessionStorage');
      await dispatch(setUserSetting( result));

    }

    dispatch(
      setTransactionNotification({
        result,
        type,
        status: hasError ? FAILED : LOADED,
      }),
    );
  } else {
    dispatch(
      setTransactionNotification({
        type,
        status: FAILED,
      }),
    );
  }

  dispatch(setWaitForTransaction({ type, pending: false }));

  const balance = await dbet.accountBalance();
  const channelBalance = await dbet.channelBalance();
  hasChannel = await dbet.hasChannel();

  dispatch(setFinalTransaction({ type }));

  await dispatch(setUserSetting({ balance, channelBalance, hasChannel }));
};

export const sendTo = payload => async dispatch => {
  const { address, max, estimate = false, amount } = payload;

  dispatch(
    setWaitForTransaction({
      type: SEND_TO,
      pending: true,
    }),
  );

  const sendTo = await dbet.send(address, amount, estimate);

  if (sendTo.hasError) {
    dispatch(
      setWaitForTransaction({
        type: SEND_TO,
        pending: false,
      }),
    );

    return { hasError: true, errorId: sendTo.errorId };
  }

  if (estimate) {
    dispatch(setContractTransaction({ ...sendTo.result, max }));

    dispatch(
      setWaitForTransaction({
        type: SEND_TO,
        pending: false,
      }),
    );
  } else {
    dispatch(setTransactionByType({ type: SEND_TO, transaction: sendTo }));
  }
};

export const sync =
  (payload = {}) =>
  async dispatch => {
    const { estimate = false } = payload;

    dispatch(
      setWaitForTransaction({
        type: SYNC,
        pending: true,
      }),
    );

    const sync = await dbet.sync(estimate);

    if (sync.hasError) {
      dispatch(
        setWaitForTransaction({
          type: SYNC,
          pending: false,
        }),
      );

      return { hasError: true, errorId: sync.errorId };
    }

    if (estimate) {
      dispatch(setContractTransaction(sync.result));

      dispatch(
        setWaitForTransaction({
          type: SYNC,
          pending: false,
        }),
      );
      return sync.result;
    } else {
      dispatch(setFinalTransaction({ type: SYNC, transaction: sync.result }));
      dispatch(setTransactionByType({ type: SYNC, transaction: sync }));
    }
  };

export const createERC20Channel = () => async dispatch => {
  dispatch(
    setWaitForTransaction({
      type: CREATE_CHANNEL,
      pending: true,
    }),
  );

  const response = await dbet.erc20CreateChannel();

  if (response.hasError) {
    dispatch(
      setWaitForTransaction({
        type: CREATE_CHANNEL,
        pending: false,
      }),
    );

    return { hasError: true, errorId: response.errorId };
  }

  await dispatch(setTransactionByType({ type: CREATE_CHANNEL, transaction: response }));

  dispatch(setUserSetting({ hasChannel: true }));
};

export const addNetworkToMetamask = () => async dispatch => {
  const response = await dbet.addNetworkToMetamask();

  if (response?.hasError) {
    dispatch(setError({ errorId: response.errorId, error: response.error }));
  }
};

export const switchNetwork = () => async dispatch => {
  const response = await dbet.switchNetwork();

  if (response?.hasError) {
    dispatch(setError({ errorId: response.errorId, error: response.error }));
  }
};

export const deposit = payload => async dispatch => {
  const { depositAmount, max, estimate = false } = payload;

  dispatch(
    setWaitForTransaction({
      type: DEPOSIT,
      pending: true,
    }),
  );

  const deposit = await dbet.deposit(depositAmount, estimate);

  if (deposit.hasError) {
    dispatch(
      setWaitForTransaction({
        type: DEPOSIT,
        pending: false,
      }),
    );

    return { hasError: true, errorId: deposit.errorId };
  }

  if (estimate) {
    dispatch(setContractTransaction({ ...deposit.result, max }));

    dispatch(
      setWaitForTransaction({
        type: DEPOSIT,
        pending: false,
      }),
    );
  } else {
    dispatch(setTransactionByType({ type: DEPOSIT, transaction: deposit }));
  }
};

export const prepareWithdraw = payload => async dispatch => {
  const { withdrawValue, max, estimate = false } = payload;

  dispatch(
    setWaitForTransaction({
      type: PREPARE_WITHDRAW,
      pending: true,
    }),
  );

  const prepareWithdraw = await dbet.prepareWithdraw(withdrawValue, estimate);
  if (prepareWithdraw.hasError) {
    dispatch(setError({ errorId: prepareWithdraw.errorId, error: prepareWithdraw.error }));
    dispatch(
      setWaitForTransaction({
        type: PREPARE_WITHDRAW,
        pending: false,
      }),
    );

    return { hasError: true, errorId: prepareWithdraw.errorId };
  }

  if (estimate) {
    dispatch(
      setContractTransaction({
        ...prepareWithdraw.result,
        amount: withdrawValue,
        max,
      }),
    );

    dispatch(
      setWaitForTransaction({
        type: PREPARE_WITHDRAW,
        pending: false,
      }),
    );
  } else {
    dispatch(setTransactionByType({ type: PREPARE_WITHDRAW, transaction: prepareWithdraw }));
  }
};

export const withdrawNow = payload => async dispatch => {
  const { withdrawValue, estimate = false, max } = payload;

  dispatch(
    setWaitForTransaction({
      type: WITHDRAW,
      pending: true,
    }),
  );

  const withdraw = await dbet.withdraw(estimate);

  if (withdraw.hasError) {
    dispatch(
      setWaitForTransaction({
        type: WITHDRAW,
        pending: false,
      }),
    );

    return { hasError: true, errorId: withdraw.errorId };
  }

  if (estimate) {
    dispatch(
      setContractTransaction({
        ...withdraw.result,
        amount: withdrawValue,
        max,
      }),
    );

    dispatch(
      setWaitForTransaction({
        type: WITHDRAW,
        pending: false,
      }),
    );
  } else {
    dispatch(setTransactionByType({ type: WITHDRAW, transaction: withdraw }));
  }
};

export const cancelWithdraw = () => async dispatch => {
  dispatch(
    setWaitForTransaction({
      type: CANCEL_WITHDRAW,
      pending: true,
    }),
  );

  const transaction = await dbet.prepareWithdraw(0);

  dispatch(setTransactionByType({ type: CANCEL_WITHDRAW, transaction }));
};
